import { useState, useEffect } from "react";
import { Canal } from "@/entities/Canal";
import { Tv, Radio, Search, RefreshCw } from "lucide-react";

const CATEGORIAS = ["Todos", "Música", "Noticias", "Deportes", "Entretenimiento", "Variedad"];

export default function TvEnVivo() {
  const [canales, setCanales] = useState([]);
  const [canalActivo, setCanalActivo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [categoria, setCategoria] = useState("Todos");
  const [busqueda, setBusqueda] = useState("");

  useEffect(() => {
    cargarCanales();
  }, []);

  async function cargarCanales() {
    setCargando(true);
    const data = await Canal.filter({ activo: true }, "orden", 100);
    setCanales(data);
    if (data.length > 0) setCanalActivo(data[0]);
    setCargando(false);
  }

  const canalesFiltrados = canales.filter((c) => {
    const matchCat = categoria === "Todos" || c.categoria === categoria;
    const matchBus = c.nombre?.toLowerCase().includes(busqueda.toLowerCase());
    return matchCat && matchBus;
  });

  function getEmbedUrl(url) {
    if (!url) return "";
    // YouTube watch -> embed
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;
    // YouTube embed ya
    if (url.includes("youtube.com/embed")) return url;
    return url;
  }

  return (
    <div className="flex flex-col h-full min-h-screen bg-background">
      {/* Header */}
      <div className="bg-primary text-primary-foreground px-6 py-4 flex items-center gap-3">
        <Tv size={24} />
        <div>
          <h1 className="text-xl font-black">TV en Vivo</h1>
          <p className="text-xs text-white/70">Canales disponibles en tiempo real</p>
        </div>
        <div className="ml-auto flex items-center gap-2 bg-accent text-accent-foreground rounded px-3 py-1">
          <Radio size={12} className="animate-pulse" />
          <span className="text-xs font-bold">EN VIVO</span>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row flex-1 gap-0">
        {/* Player principal */}
        <div className="flex-1 bg-black flex flex-col">
          {canalActivo ? (
            <>
              <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                <iframe
                  key={canalActivo.id}
                  src={getEmbedUrl(canalActivo.url_stream)}
                  className="absolute inset-0 w-full h-full"
                  allowFullScreen
                  allow="autoplay; encrypted-media"
                  title={canalActivo.nombre}
                />
              </div>
              <div className="bg-gray-900 text-white px-4 py-3">
                <div className="flex items-center gap-3">
                  {canalActivo.imagen && (
                    <img src={canalActivo.imagen} alt={canalActivo.nombre} className="h-10 w-10 rounded object-cover" />
                  )}
                  <div>
                    <p className="font-bold text-sm">{canalActivo.nombre}</p>
                    <p className="text-xs text-gray-400">{canalActivo.descripcion}</p>
                  </div>
                  <span className="ml-auto text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                    {canalActivo.categoria}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-white/40 flex-col gap-3 py-20">
              <Tv size={48} />
              <p className="text-lg">Selecciona un canal para ver</p>
            </div>
          )}
        </div>

        {/* Lista de canales */}
        <div className="w-full lg:w-72 bg-gray-900 flex flex-col">
          <div className="p-3 border-b border-white/10">
            <div className="relative mb-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar canal..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full bg-gray-800 text-white text-sm pl-8 pr-3 py-2 rounded border border-white/10 focus:outline-none focus:border-primary"
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {CATEGORIAS.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoria(cat)}
                  className={`text-xs px-2 py-1 rounded transition-all ${
                    categoria === cat
                      ? "bg-primary text-primary-foreground"
                      : "bg-gray-800 text-gray-400 hover:bg-gray-700"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {cargando ? (
              <div className="flex items-center justify-center py-10 text-gray-400">
                <RefreshCw size={20} className="animate-spin mr-2" />
                Cargando...
              </div>
            ) : canalesFiltrados.length === 0 ? (
              <div className="text-center py-10 text-gray-500 text-sm">No hay canales disponibles</div>
            ) : (
              canalesFiltrados.map((canal) => (
                <button
                  key={canal.id}
                  onClick={() => setCanalActivo(canal)}
                  className={`w-full flex items-center gap-3 px-3 py-3 border-b border-white/5 transition-all text-left
                    ${canalActivo?.id === canal.id ? "bg-primary/30 border-l-4 border-l-primary" : "hover:bg-white/5"}`}
                >
                  {canal.imagen ? (
                    <img src={canal.imagen} alt={canal.nombre} className="h-10 w-16 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="h-10 w-16 rounded bg-gray-700 flex items-center justify-center flex-shrink-0">
                      <Tv size={16} className="text-gray-400" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{canal.nombre}</p>
                    <p className="text-gray-400 text-xs truncate">{canal.categoria}</p>
                  </div>
                  {canalActivo?.id === canal.id && (
                    <Radio size={12} className="text-accent animate-pulse ml-auto flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}